import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Fragment, Slice, Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { NodeType, Schema } from '@tiptap/pm/model';
import { markSystemTr } from './shared';
import { ZWSP } from './zwsp';

export { ZWSP } from './zwsp';

type TZwspPluginOptions = {
  /**
   * When true, pressing Backspace while inside a node that contains only ZWSP
   * will delete the entire node. Use for caption-like nodes that should be
   * removable. For locked nodes (e.g. template fields), leave false.
   * @default false
   */
  deleteNodeOnBackspace?: boolean;
  /** Unique key for the plugin. Defaults to `zwsp-<nodeType.name>`. */
  pluginKey?: string;
};

type TFix = { from: number; to: number; text: string } | { from: number; to: number; content: Fragment };

/**
 * Recursively strip all ZWSP characters from text nodes while preserving
 * marks, inline nodes, and block structure.
 *
 * `tr.insertText` cannot be used for ZWSP cleanup because it replaces the
 * entire range with a single plain-text node, losing marks and inline nodes.
 * This helper rebuilds the fragment without ZWSP so `tr.replace` can swap
 * the content non-destructively.
 */
const removeZwspFromFragment = (fragment: Fragment, schema: Schema): Fragment => {
  const nodes: ProseMirrorNode[] = [];
  fragment.forEach((child) => {
    if (child.isText && child.text) {
      const cleaned = child.text.split(ZWSP).join('');
      if (cleaned) nodes.push(schema.text(cleaned, child.marks));
    } else if (child.isLeaf) {
      nodes.push(child);
    } else {
      nodes.push(child.copy(removeZwspFromFragment(child.content, schema)));
    }
  });
  return Fragment.fromArray(nodes);
};

/**
 * Generic ZWSP (Zero Width Space) maintenance plugin.
 *
 * Keeps a U+200B character inside every empty node of `nodeType` so that
 * an empty contenteditable inline element always has a cursor anchor - fixes
 * Safari's "cursor disappears in empty inline element" issue.
 *
 * Behaviour:
 * - Empty node → insert ZWSP
 * - Node with real content + ZWSP → strip all ZWSPs (preserving marks & inline nodes)
 * - Node with ZWSP only → no change (already correct)
 * - deleteNodeOnBackspace=true: Backspace/Delete on a ZWSP-only node deletes the entire node
 *
 * Used by:
 * - getCaptionZwspPlugin (deleteNodeOnBackspace: true)
 * - TemplateFieldInlineNode (deleteNodeOnBackspace: false)
 */
const getZwspPlugin = (nodeType: NodeType, options: TZwspPluginOptions = {}): Plugin => {
  const { deleteNodeOnBackspace = false, pluginKey: keyName = `zwsp-${nodeType.name}` } = options;

  return new Plugin({
    key: new PluginKey(keyName),

    props: deleteNodeOnBackspace
      ? {
          handleKeyDown(view, event) {
            if (!view.editable) return false;
            if (event.key !== 'Backspace' && event.key !== 'Delete') return false;

            const { $from } = view.state.selection;
            const depth = $from.depth;
            if (depth < 1) return false;

            const node = $from.node(depth);
            if (node.type !== nodeType) return false;

            const text = node.textContent;
            if (text !== ZWSP && text !== '') return false;

            const tr = view.state.tr.delete($from.before(depth), $from.after(depth));
            view.dispatch(tr);
            return true;
          },
        }
      : {},

    appendTransaction(transactions, _oldState, newState) {
      if (!transactions.some((t) => t.docChanged)) return null;

      const fixes: TFix[] = [];

      newState.doc.descendants((node, pos) => {
        if (node.type !== nodeType) return; // not our node; keep descending to find it

        const text = node.textContent;

        if (text === '') {
          fixes.push({ from: pos + 1, to: pos + 1, text: ZWSP });
          return false; // no need to descend into an empty node
        }

        if (text !== ZWSP && text.includes(ZWSP)) {
          const cleaned = removeZwspFromFragment(node.content, newState.schema);
          if (cleaned.size > 0) {
            fixes.push({ from: pos + 1, to: pos + node.nodeSize - 1, content: cleaned });
          } else {
            // All content was ZWSP - keep one ZWSP as cursor anchor
            fixes.push({ from: pos + 1, to: pos + node.nodeSize - 1, text: ZWSP });
          }
        }
        return false; // don't descend into field's own children
      });

      if (!fixes.length) return null;

      let tr = newState.tr;
      // Apply end-to-start to keep positions valid
      for (let i = fixes.length - 1; i >= 0; i--) {
        const fix = fixes[i];
        if ('content' in fix) {
          tr = tr.replace(fix.from, fix.to, new Slice(fix.content, 0, 0));
        } else {
          tr = tr.insertText(fix.text, fix.from, fix.to);
        }
      }
      return markSystemTr(tr);
    },
  });
};

export default getZwspPlugin;
